// RegO shield brand mark with theme-aware gradient palettes.
import React from "react";

interface ShieldPalette {
  g1: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  g2: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  g3: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  g4: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  g5: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  g6: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  check: string;
  checkShadow: string;
}

const LIGHT_SHIELD_PALETTE: ShieldPalette = {
  g1: [
    { offset: "0%", color: "#2B378C" },
    { offset: "51%", color: "#2E3285" },
    { offset: "86%", color: "#372C77" },
    { offset: "100%", color: "#3D2A70" },
  ],
  g2: [
    { offset: "7%", color: "#882783" },
    { offset: "50%", color: "#314196" },
    { offset: "93%", color: "#364F9F" },
  ],
  g3: [
    { offset: "0%", color: "#261E58" },
    { offset: "50%", color: "#455CA4" },
    { offset: "100%", color: "#261E58" },
  ],
  g4: [
    { offset: "0%", color: "#D684B7" },
    { offset: "100%", color: "#832573" },
  ],
  g5: [
    { offset: "0%", color: "#372D77" },
    { offset: "68%", color: "#455CA4" },
    { offset: "100%", color: "#455CA4" },
  ],
  g6: [
    { offset: "0%", color: "#842573" },
    { offset: "100%", color: "#D684B7" },
  ],
  check: "white",
  checkShadow: "drop-shadow(1px 2px 2px rgba(0,0,0,0.47))",
};

const DARK_SHIELD_PALETTE: ShieldPalette = {
  g1: [
    { offset: "0%", color: "#818cf8" },
    { offset: "51%", color: "#8b7cf6" },
    { offset: "86%", color: "#a78bfa" },
    { offset: "100%", color: "#c084fc" },
  ],
  g2: [
    { offset: "7%", color: "#e879f9" },
    { offset: "50%", color: "#a5b4fc" },
    { offset: "93%", color: "#818cf8" },
  ],
  g3: [
    { offset: "0%", color: "#6366f1" },
    { offset: "50%", color: "#a5b4fc" },
    { offset: "100%", color: "#4f46e5" },
  ],
  g4: [
    { offset: "0%", color: "#f0abfc" },
    { offset: "100%", color: "#d946ef" },
  ],
  g5: [
    { offset: "0%", color: "#8b5cf6" },
    { offset: "68%", color: "#a5b4fc" },
    { offset: "100%", color: "#a5b4fc" },
  ],
  g6: [
    { offset: "0%", color: "#d946ef" },
    { offset: "100%", color: "#f0abfc" },
  ],
  check: "white",
  checkShadow: "drop-shadow(1px 2px 3px rgba(0,0,0,0.6))",
};

export function RegoShieldMark({ size = 48 }: { size?: number }) {
  return (
    <>
      <div className="dark:hidden">
        <RegoShieldSvg
          size={size}
          idSuffix="light"
          shadow="drop-shadow(0 8px 18px rgba(99, 102, 241, 0.35))"
          palette={LIGHT_SHIELD_PALETTE}
        />
      </div>
      <div className="hidden dark:block">
        <RegoShieldSvg
          size={size}
          idSuffix="dark"
          shadow="drop-shadow(0 8px 22px rgba(168, 139, 250, 0.45))"
          palette={DARK_SHIELD_PALETTE}
        />
      </div>
    </>
  );
}

function RegoShieldSvg({
  size,
  idSuffix,
  shadow,
  palette,
}: {
  size: number;
  idSuffix: string;
  shadow: string;
  palette: ShieldPalette;
}) {
  const id = (n: number) => `rgc-shield-${n}-${idSuffix}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      style={{ filter: shadow }}
    >
      <defs>
        <linearGradient id={id(1)} x1="0%" y1="100%" x2="100%" y2="0%">
          {palette.g1.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        <linearGradient id={id(2)} x1="100%" y1="0%" x2="0%" y2="100%">
          {palette.g2.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        <radialGradient id={id(3)} cx="37%" cy="43%" r="81%">
          {palette.g3.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(4)} cx="40%" cy="38%" r="76%">
          {palette.g4.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(5)} cx="18%" cy="56%" r="58%">
          {palette.g5.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(6)} cx="74%" cy="67%" r="56%">
          {palette.g6.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
      </defs>
      <path
        d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
        fill={`url(#${id(1)})`}
      />
      <path
        d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
        fill={`url(#${id(2)})`}
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill={`url(#${id(3)})`}
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill={`url(#${id(4)})`}
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill={`url(#${id(5)})`}
        opacity="0.7"
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill={`url(#${id(6)})`}
        opacity="0.7"
      />
      <path
        d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
        fill={palette.check}
        style={{ filter: palette.checkShadow }}
      />
    </svg>
  );
}
